/**
 * How reminder channels read, which ones a reminder can switch between, and
 * what the reminder settings say, shared by web and native so the settings
 * switches and the reminder panel say the same thing on both.
 */

import type { ReminderChannel } from "@/types/calendarEvent.ts";
import type { UserPreferencesRequired } from "@/types/userPreferences.ts";

export const REMINDER_CHANNEL_LABELS: Record<ReminderChannel, string> = {
  text: "Text",
  whatsapp: "WhatsApp",
  app: "In app",
  email: "Email",
};

/**
 * What the panel offers until the server's list arrives, or when asking for it
 * failed: the two channels every reminder had before WhatsApp and in-app. A
 * pick the server can't honour is refused with its reason, which beats a panel
 * that reads as if the reminder can't be changed at all.
 */
export const REMINDER_BASELINE_CHANNELS: readonly ReminderChannel[] = ["text", "email"];

/**
 * The channels a reminder for someone else can switch between. Both go to a
 * phone number, so the stored handle still reaches them on either; an email
 * address reaches them only by email, and `app` is a push to the user's own
 * devices.
 */
export const PHONE_REMINDER_CHANNELS: readonly ReminderChannel[] = ["text", "whatsapp"];

/**
 * `channels` with `current` appended when it isn't among them. A stored channel
 * that has since stopped reaching anyone (WhatsApp disconnected) is still the
 * saved state, and dropping it would leave a picker showing a value that isn't
 * among its options.
 */
export function withCurrentChannel(
  channels: readonly ReminderChannel[],
  current: ReminderChannel | undefined,
): ReminderChannel[] {
  return current && !channels.includes(current) ? [...channels, current] : [...channels];
}

/**
 * The channels a reminder's panel offers, from `availableChannels` (the
 * server's list of what reaches the user now). A reminder to the user can go
 * anywhere on it; one for other people only to the phone channels on it, since
 * okthx WhatsApps others only from an owner whose own WhatsApp it can use, and
 * only where the address the reminder holds is a phone number.
 */
export function reminderPanelChannels({
  current,
  isSelfReminder,
  availableChannels,
}: {
  current: ReminderChannel;
  isSelfReminder: boolean;
  availableChannels: readonly ReminderChannel[];
}): ReminderChannel[] {
  if (!isSelfReminder) {
    if (!PHONE_REMINDER_CHANNELS.includes(current)) return [current];
    const phone = PHONE_REMINDER_CHANNELS.filter((channel) => availableChannels.includes(channel));
    return withCurrentChannel(phone, current);
  }
  return withCurrentChannel(availableChannels, current);
}

type NotificationPreferences = UserPreferencesRequired["notifications"];

/** The order the reminder settings list the channels in. */
export const REMINDER_SETTINGS_CHANNELS: readonly ReminderChannel[] = [
  "text",
  "whatsapp",
  "app",
  "email",
];

/** The `notifications.reminders` key that switches each channel. */
const REMINDER_SETTING_KEYS = {
  text: "sms",
  whatsapp: "whatsapp",
  app: "app",
  email: "email",
} as const satisfies Record<ReminderChannel, keyof NotificationPreferences["reminders"]>;

/** Whether reminders may go out on `channel`. */
export function isReminderChannelOn(
  notifications: NotificationPreferences,
  channel: ReminderChannel,
): boolean {
  return notifications.reminders[REMINDER_SETTING_KEYS[channel]];
}

/** The notifications delta that switches reminders on `channel` on or off. */
export function reminderChannelDelta(
  channel: ReminderChannel,
  on: boolean,
): { reminders: Partial<NotificationPreferences["reminders"]> } {
  return { reminders: { [REMINDER_SETTING_KEYS[channel]]: on } };
}

/**
 * Whether `channel` reaches the user, for the hint under its switch. Text needs
 * a verified phone. The rest go by `available`, the server's list; until it
 * arrives, or if asking for it failed, they read as reachable rather than
 * flashing a warning.
 */
export function isReminderChannelReachable(
  channel: ReminderChannel,
  { hasVerifiedPhone, available }: { hasVerifiedPhone: boolean; available: readonly ReminderChannel[] | undefined },
): boolean {
  return channel === "text" ? hasVerifiedPhone : (available?.includes(channel) ?? true);
}

/** What each channel's switch says, and what it says while the channel can't reach the user. */
const REMINDER_CHANNEL_HINTS: Record<ReminderChannel, { reachable: string; unreachable: string }> = {
  text: {
    reachable: "Text replies come straight back to Okthx.",
    unreachable: "Verify your phone number to get reminders by text.",
  },
  whatsapp: {
    reachable: "From Okthx's WhatsApp number.",
    unreachable: "Connect WhatsApp to get reminders there.",
  },
  app: {
    reachable: "A push notification on your phone or in the browser.",
    unreachable: "Turn on notifications in the Okthx app or your browser to get reminders there.",
  },
  email: {
    reachable: "Get the reminder in your inbox.",
    unreachable: "Get the reminder in your inbox.",
  },
};

/** The note under a channel's switch. */
export function reminderChannelHint(channel: ReminderChannel, reachable: boolean): string {
  const hints = REMINDER_CHANNEL_HINTS[channel];
  return reachable ? hints.reachable : hints.unreachable;
}

/** The note under the reminder settings' heading. */
export const REMINDER_SETTINGS_DESCRIPTION =
  "Where Okthx sends reminders you set. Each goes where you asked when that's on, otherwise the first one on. Naming a channel for a reminder always works.";
